<template>
  <div class="space-y-6">
    <!-- Empty state -->
    <div v-if="sellers.length === 0" class="text-center py-12">
      <p class="text-gray-500">Ingen data tillgänglig för Säljtavla</p>
    </div>

    <template v-else>
      <!-- Header: seller + budget -->
      <div class="flex items-center justify-between flex-wrap gap-4">
        <h2 class="text-2xl font-bold">Säljtavla</h2>
        <div class="flex items-center gap-6 flex-wrap">
          <div class="flex items-center gap-3">
            <label class="font-medium">Säljare:</label>
            <select v-model="selectedSeller" class="border border-gray-300 rounded-lg px-4 py-2 text-sm min-w-[200px]">
              <option v-for="s in sellers" :key="s" :value="s">{{ s }}</option>
            </select>
          </div>
          <div class="flex items-center gap-3">
            <label class="font-medium">Budget:</label>
            <template v-if="isEditingBudget">
              <input
                v-model="budgetInput"
                type="number"
                placeholder="Ange budget (SEK)"
                class="border border-gray-300 rounded px-2 py-1 text-sm w-40"
                @keydown.enter="saveBudget"
                @keydown.esc="isEditingBudget = false"
              />
              <button class="sd-btn sd-btn--primary text-xs" @click="saveBudget">Spara</button>
              <button class="sd-btn sd-btn--secondary text-xs" @click="isEditingBudget = false">Avbryt</button>
            </template>
            <template v-else>
              <span class="font-semibold text-lg">{{ kpis.budget > 0 ? formatNumber(kpis.budget) : '–' }} SEK</span>
              <button class="sd-btn sd-btn--secondary text-xs" @click="startEditBudget">{{ kpis.budget > 0 ? 'Ändra' : 'Sätt budget' }}</button>
            </template>
          </div>
        </div>
      </div>

      <!-- Sales Velocity -->
      <div class="bg-white rounded-lg shadow p-6">
        <h3 class="text-lg font-semibold mb-4">Sales Velocity</h3>
        <div class="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
          <div class="p-4 rounded border bg-gray-50 cursor-pointer hover:bg-gray-100" @click="startEdit('Meetings')">
            <p class="text-xs text-gray-600 mb-1">Möten/vecka</p>
            <input
              v-if="editing === 'Meetings'"
              ref="editInput"
              v-model="tempValue"
              type="number"
              min="0"
              class="text-2xl font-bold text-center w-full p-1 border rounded"
              @blur="saveVelocity"
              @keydown.enter="saveVelocity"
              @keydown.esc="editing = null"
            />
            <p v-else class="text-2xl font-bold text-gray-900">{{ velocity.meetingsPerWeek || '–' }}</p>
          </div>

          <div class="p-4 rounded border bg-gray-50 cursor-pointer hover:bg-gray-100" @click="startEdit('HitRate')">
            <p class="text-xs text-gray-600 mb-1">Hit Rate</p>
            <input
              v-if="editing === 'HitRate'"
              ref="editInput"
              v-model="tempValue"
              type="number"
              min="0"
              max="100"
              class="text-2xl font-bold text-center w-full p-1 border rounded"
              @blur="saveVelocity"
              @keydown.enter="saveVelocity"
              @keydown.esc="editing = null"
            />
            <p v-else class="text-2xl font-bold text-gray-900">{{ velocity.hitRate ? velocity.hitRate + '%' : '–' }}</p>
          </div>

          <div class="p-4 rounded border bg-gray-50 cursor-pointer hover:bg-gray-100" @click="startEdit('AvgDeal')">
            <p class="text-xs text-gray-600 mb-1">Snittaffär (SEK)</p>
            <input
              v-if="editing === 'AvgDeal'"
              ref="editInput"
              v-model="tempValue"
              type="number"
              min="0"
              class="text-2xl font-bold text-center w-full p-1 border rounded"
              @blur="saveVelocity"
              @keydown.enter="saveVelocity"
              @keydown.esc="editing = null"
            />
            <p v-else class="text-2xl font-bold text-gray-900">{{ velocity.averageDealValue ? formatNumber(velocity.averageDealValue) : '–' }}</p>
          </div>

          <div class="p-4 rounded border bg-blue-50 border-blue-200">
            <p class="text-xs text-gray-600 mb-1">Förväntade affärer/vecka</p>
            <p class="text-2xl font-bold text-blue-600">{{ velocityHasData ? velocityMetrics.expectedDeals.toFixed(1) : '–' }}</p>
          </div>
          <div class="p-4 rounded border bg-green-50 border-green-200">
            <p class="text-xs text-gray-600 mb-1">Förväntad oms./vecka</p>
            <p class="text-2xl font-bold text-green-600">{{ velocityHasData ? formatNumber(velocityMetrics.expectedRevenueWeek) : '–' }}</p>
            <p class="text-xs text-gray-500 mt-1">SEK</p>
          </div>
          <div class="p-4 rounded border" style="background-color: #ECFDF5; border-color: #A7F3D0">
            <p class="text-xs text-gray-600 mb-1">Förväntad oms./månad</p>
            <p class="text-2xl font-bold" style="color: #047857">{{ velocityHasData ? formatNumber(velocityMetrics.expectedRevenueMonth) : '–' }}</p>
            <p class="text-xs text-gray-500 mt-1">SEK</p>
          </div>
        </div>
        <p v-if="!velocityHasData" class="text-xs text-gray-500 mt-4 italic">
          💡 Klicka på korten för att ange värden: Möten/vecka, Hit Rate och Snittaffär.
        </p>
      </div>

      <!-- Forecast Section -->
      <div class="bg-white rounded-lg shadow p-6">
        <div class="flex items-center justify-between mb-4 flex-wrap gap-3">
          <h3 class="text-lg font-semibold">Forecast</h3>
          <div class="flex gap-2 flex-wrap">
            <button
              class="sd-btn text-xs"
              :class="selectionMode === 'commit' ? 'sd-btn--primary' : 'sd-btn--secondary'"
              @click="selectionMode = selectionMode === 'commit' ? null : 'commit'"
            >Markera Commit</button>
            <button
              class="sd-btn text-xs"
              :class="selectionMode === 'risk' ? 'sd-btn--primary' : 'sd-btn--secondary'"
              :style="selectionMode === 'risk' ? 'background-color:#ea580c;border-color:#ea580c' : ''"
              @click="selectionMode = selectionMode === 'risk' ? null : 'risk'"
            >Markera Risk</button>
            <button
              class="sd-btn text-xs"
              :class="selectionMode === 'bestCase' ? 'sd-btn--primary' : 'sd-btn--secondary'"
              :style="selectionMode === 'bestCase' ? 'background-color:#16a34a;border-color:#16a34a' : ''"
              @click="selectionMode = selectionMode === 'bestCase' ? null : 'bestCase'"
            >Markera Best Case</button>
            <button v-if="forecastHasData" class="sd-btn sd-btn--secondary text-xs text-red-600" @click="resetForecasts">
              <BaseIcon icon="redo" class="mr-1" />Nollställ
            </button>
          </div>
        </div>
        <div class="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div class="space-y-3">
            <h4 class="text-sm font-semibold text-gray-600 mb-3">Valda affärer</h4>
            <div class="flex justify-between items-center">
              <span class="text-sm text-gray-600">Commit:</span>
              <span class="text-sm font-semibold">{{ formatNumber(forecastCalculations.commitValue) }} SEK</span>
            </div>
            <div class="flex justify-between items-center">
              <span class="text-sm text-gray-600">Risk i Commit:</span>
              <span class="text-sm font-semibold">{{ formatNumber(forecastCalculations.riskValue) }} SEK</span>
            </div>
            <div>
              <div class="flex justify-between items-center">
                <span class="text-sm text-gray-600 inline-flex items-center gap-2">
                  Strong Best Case:
                  <BaseIcon v-if="bestCaseWarning" icon="exclamation-triangle" class="text-red-600" />
                </span>
                <span class="text-sm font-semibold">{{ formatNumber(forecastCalculations.bestCaseValue) }} SEK</span>
              </div>
              <p v-if="bestCaseWarning" class="text-xs text-red-600 mt-1 italic">
                Uppsidan är lägre än risken - du behöver fler back-up case
              </p>
            </div>
          </div>
          <div class="md:col-span-2">
            <h4 class="text-sm font-semibold text-gray-600 mb-3">Prognoser</h4>
            <div class="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div class="p-4 rounded border bg-blue-50 border-blue-200">
                <p class="text-xs text-gray-600 mb-1">Commit + Vunnen</p>
                <p class="text-xl font-bold text-blue-600">{{ formatNumber(forecastCalculations.commitPlusWon) }}</p>
                <p class="text-xs text-gray-500 mt-1">SEK</p>
              </div>
              <div class="p-4 rounded border bg-orange-50 border-orange-200">
                <p class="text-xs text-gray-600 mb-1">Worst Case</p>
                <p class="text-xl font-bold text-orange-600">{{ formatNumber(forecastCalculations.worstCase) }}</p>
                <p class="text-xs text-gray-500 mt-1">SEK</p>
              </div>
              <div class="p-4 rounded border bg-green-50 border-green-200">
                <p class="text-xs text-gray-600 mb-1">Best Case</p>
                <p class="text-xl font-bold text-green-600">{{ formatNumber(forecastCalculations.bestCase) }}</p>
                <p class="text-xs text-gray-500 mt-1">SEK</p>
              </div>
            </div>
          </div>
        </div>
        <p v-if="selectionMode" class="text-sm text-gray-600 mt-4 italic">
          💡 Klicka på affärsmöjligheter nedan för att markera dem som
          {{ selectionMode === 'commit' ? 'Commit' : selectionMode === 'risk' ? 'Risk i Commit' : 'Strong Best Case' }}
        </p>
      </div>

      <!-- Main Grid -->
      <div class="grid grid-cols-12 gap-4">
        <!-- Left: First Meetings -->
        <div class="col-span-12 lg:col-span-2">
          <div class="bg-white rounded-lg shadow p-4">
            <h3 class="font-semibold text-sm mb-3 flex items-center gap-2">
              <BaseIcon icon="calendar-alt" /> Förstamöten
            </h3>
            <p class="text-xs text-gray-600 mb-4">{{ firstMeetings.length }} bokade</p>
            <div class="space-y-2 overflow-y-auto pr-1" style="max-height: calc(100vh - 260px); min-height: 400px;">
              <p v-if="firstMeetings.length === 0" class="text-xs text-gray-400 italic">Inga kommande förstamöten</p>
              <div
                v-for="(m, i) in firstMeetings"
                :key="`fm-${i}`"
                class="p-2 bg-gray-50 rounded border border-gray-200 hover:bg-gray-100 cursor-pointer"
                @click="selectedItem = { type: 'meeting', data: m }"
              >
                <div class="flex items-center gap-1 text-xs text-gray-500 mb-1">
                  <BaseIcon icon="calendar-alt" />
                  {{ formatDate(m['Mötesdatum']) }}
                </div>
                <div class="flex items-center gap-1 text-xs font-medium mb-1">
                  <BaseIcon icon="building" class="text-gray-400" />
                  <span class="truncate">{{ m['Företag'] || 'Okänt företag' }}</span>
                </div>
                <p v-if="m['Beskrivning']" class="text-xs text-gray-600 truncate" :title="m['Beskrivning']">
                  {{ m['Beskrivning'] }}
                </p>
              </div>
            </div>
          </div>
        </div>

        <!-- Middle: Pipeline phases -->
        <div class="col-span-12 lg:col-span-8 grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-3">
          <div v-for="(opps, phase) in opportunitiesByPhase" :key="phase" class="flex flex-col">
            <div class="bg-white rounded-lg shadow p-3 flex-1 flex flex-col">
              <div class="mb-3">
                <h3 class="font-semibold text-sm mb-1 truncate" :title="phase">{{ phase }}</h3>
                <p class="text-lg font-bold text-blue-600">{{ formatNumber(phaseTotals[phase]) }} SEK</p>
                <p class="text-xs text-gray-500">{{ opps.length }} affärer</p>
              </div>
              <div class="space-y-1.5 flex-1 overflow-y-auto pr-1" style="max-height: calc(100vh - 220px); min-height: 400px;">
                <div
                  v-for="(opp, i) in opps"
                  :key="`${phase}-${i}`"
                  class="p-2 rounded border cursor-pointer hover:opacity-90 transition-all"
                  :class="cardColorClass(opp['Avslutsdatum'], phase)"
                  @click="onOppClick(opp, phase)"
                >
                  <div class="flex items-center gap-1 mb-1">
                    <BaseIcon
                      :icon="phase === 'Vunnen' ? 'star' : 'building'"
                      :class="phase === 'Vunnen' ? 'text-yellow-600' : 'text-gray-400'"
                      style="font-size:11px"
                    />
                    <span class="text-xs font-medium truncate" :title="opp['Företag']">
                      {{ opp['Företag'] || 'Okänt' }}
                    </span>
                    <span class="ml-auto inline-flex gap-0.5">
                      <span v-if="isInSelection(opp, phase, 'commit')" class="w-1.5 h-1.5 rounded-full bg-blue-500"></span>
                      <span v-if="isInSelection(opp, phase, 'risk')" class="w-1.5 h-1.5 rounded-full bg-orange-500"></span>
                      <span v-if="isInSelection(opp, phase, 'bestCase')" class="w-1.5 h-1.5 rounded-full bg-green-500"></span>
                    </span>
                  </div>
                  <p class="text-xs font-semibold text-gray-700">
                    {{ formatNumber(Number(opp['Ordervärde']) || 0) }} SEK
                  </p>
                  <p v-if="opp['Beskrivning']" class="text-[11px] text-gray-600 line-clamp-2 mt-0.5" :title="opp['Beskrivning']">
                    {{ opp['Beskrivning'] }}
                  </p>
                  <p v-if="opp['Avslutsdatum']" class="text-[11px] text-gray-500 mt-0.5">
                    {{ formatDate(opp['Avslutsdatum']) }}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Right: Forecast vs Budget -->
        <div class="col-span-12 lg:col-span-2 space-y-4">
          <template v-if="kpis.budget > 0">
            <div v-for="ring in budgetRings" :key="ring.label" class="bg-white rounded-lg shadow p-4">
              <div class="flex flex-col items-center justify-center">
                <div class="relative" style="width:96px;height:96px;">
                  <svg width="96" height="96" style="transform: rotate(-90deg)">
                    <circle cx="48" cy="48" r="40" stroke="#E5E7EB" stroke-width="8" fill="none"></circle>
                    <circle
                      cx="48" cy="48" r="40"
                      :stroke="ring.color" stroke-width="8" fill="none" stroke-linecap="round"
                      :stroke-dasharray="circumference"
                      :stroke-dashoffset="dashOffset(ring.value)"
                    ></circle>
                  </svg>
                  <div class="absolute inset-0 flex items-center justify-center">
                    <p class="text-xl font-bold" :style="{ color: ring.color }">{{ Math.round(ring.percent) }}%</p>
                  </div>
                </div>
                <p class="text-xs text-gray-600 text-center mt-2">{{ ring.label }}</p>
                <p class="text-xs text-gray-500 mt-2 text-center">
                  {{ formatNumber(ring.value) }} / {{ formatNumber(kpis.budget) }} SEK
                </p>
              </div>
            </div>
          </template>
          <div v-else class="bg-white rounded-lg shadow p-8">
            <div class="text-center text-gray-500">
              <BaseIcon icon="bullseye" class="text-3xl text-gray-400 mb-4" />
              <p class="text-sm font-medium mb-2">Ingen budget satt</p>
              <p class="text-xs">Sätt en budget för att se prognoser i förhållande till målet</p>
            </div>
          </div>
        </div>
      </div>

      <!-- Detail Modal -->
      <div v-if="selectedItem" class="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-50" @click.self="selectedItem = null">
        <div class="bg-white rounded-lg shadow-xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
          <div class="flex items-center justify-between p-4 border-b">
            <h3 class="text-lg font-semibold">
              {{ selectedItem.type === 'meeting' ? 'Förstamöte' : 'Affärsmöjlighet' }}
            </h3>
            <button class="text-gray-400 hover:text-gray-600" @click="selectedItem = null">
              <BaseIcon icon="times" />
            </button>
          </div>
          <div class="p-6 space-y-4">
            <div v-if="selectedItem.type === 'meeting'" class="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <p class="text-sm font-semibold text-gray-600">Företag</p>
                <p class="text-base">{{ selectedItem.data['Företag'] || '–' }}</p>
              </div>
              <div>
                <p class="text-sm font-semibold text-gray-600">Mötesdatum</p>
                <p class="text-base">{{ formatDateLong(selectedItem.data['Mötesdatum']) }}</p>
              </div>
              <div>
                <p class="text-sm font-semibold text-gray-600">Mötestyp</p>
                <p class="text-base">{{ selectedItem.data['Mötestyp'] || '–' }}</p>
              </div>
              <div>
                <p class="text-sm font-semibold text-gray-600">Säljare</p>
                <p class="text-base">{{ selectedItem.data['Säljare'] || '–' }}</p>
              </div>
              <div>
                <p class="text-sm font-semibold text-gray-600">Kontaktpersoner</p>
                <p class="text-base">{{ selectedItem.data['Kontaktpersoner'] || '–' }}</p>
              </div>
              <div>
                <p class="text-sm font-semibold text-gray-600">Plats</p>
                <p class="text-base">{{ selectedItem.data['Plats'] || '–' }}</p>
              </div>
              <div v-if="selectedItem.data['Beskrivning']" class="md:col-span-2">
                <p class="text-sm font-semibold text-gray-600 mb-1">Beskrivning</p>
                <p class="text-base bg-gray-50 p-3 rounded">{{ selectedItem.data['Beskrivning'] }}</p>
              </div>
            </div>
            <div v-else class="space-y-4">
              <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <p class="text-sm font-semibold text-gray-600">Företag</p>
                  <p class="text-base">{{ selectedItem.data['Företag'] || '–' }}</p>
                </div>
                <div>
                  <p class="text-sm font-semibold text-gray-600">Säljare</p>
                  <p class="text-base">{{ selectedItem.data['Säljare'] || '–' }}</p>
                </div>
                <div>
                  <p class="text-sm font-semibold text-gray-600">Ordervärde</p>
                  <p class="text-base">{{ formatNumber(Number(selectedItem.data['Ordervärde']) || 0) }} SEK</p>
                </div>
                <div>
                  <p class="text-sm font-semibold text-gray-600">Avslutsdatum</p>
                  <p class="text-base">{{ formatDateLong(selectedItem.data['Avslutsdatum']) }}</p>
                </div>
                <div>
                  <p class="text-sm font-semibold text-gray-600">Fas</p>
                  <p class="text-base">{{ selectedItem.data['Fas'] || '–' }}</p>
                </div>
                <div v-if="opportunityIdValue">
                  <p class="text-sm font-semibold text-gray-600">ID</p>
                  <p class="text-base font-mono text-xs">{{ opportunityIdValue }}</p>
                </div>
              </div>
              <div v-if="selectedItem.data['Beskrivning']">
                <p class="text-sm font-semibold text-gray-600 mb-1">Beskrivning</p>
                <p class="text-base bg-gray-50 p-3 rounded">{{ selectedItem.data['Beskrivning'] }}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </template>
  </div>
</template>

<script>
const ALLOWED_SELLERS = [
  "Troy Messinger",
  "Tilda Fransson",
  "Victor Sa",
  "Christian Meritson",
  "Marc-Lee Undseth",
  "Rickard Jiman",
  "Niclas Manestam",
  "Anton Janebrant",
];

const BUDGET_KEY = "personal_dashboard_budgets";
const SELECTED_OPPS_KEY = "personal_dashboard_selected_opportunities";
const VELOCITY_KEY = "personal_dashboard_sales_velocity";
const SALES_FORECAST_KEY = "sales_forecast_data";

const PHASE_ORDER = [
  "Fallback",
  "Kvalificering",
  "Offert/andra möte",
  "Muntlig överenskommelse",
  "Vunnen",
];

function startOfDay(d) { const x = new Date(d); x.setHours(0, 0, 0, 0); return x; }

function getSortPriority(dateStr) {
  if (!dateStr) return 3;
  const closeDate = startOfDay(new Date(dateStr));
  const now = startOfDay(new Date());
  if (closeDate.getMonth() === now.getMonth() && closeDate.getFullYear() === now.getFullYear() && closeDate >= now) return 0;
  if (closeDate < now) return 1;
  return 2;
}

function getOppId(opp, phase) {
  return `${phase}-${opp["Företag"]}-${opp["Ordervärde"]}-${opp["Avslutsdatum"]}`;
}

export default {
  name: "PipelinePersonalDashboard",
  props: {
    pipelineData: { type: Array, default: () => [] },
    wonDealsData: { type: Array, default: () => [] },
    appointments: { type: Array, default: () => [] },
  },
  data() {
    return {
      selectedSeller: "",
      budgets: {},
      budgetInput: "",
      isEditingBudget: false,
      selectedOpportunities: {}, // { seller: { commit: Set, risk: Set, bestCase: Set } }
      selectionMode: null,
      salesVelocity: {}, // { seller: { meetingsPerWeek, hitRate, averageDealValue } }
      editing: null, // 'Meetings' | 'HitRate' | 'AvgDeal'
      tempValue: "",
      selectedItem: null,
      circumference: 2 * Math.PI * 40,
    };
  },
  computed: {
    sellers() {
      const set = new Set();
      this.pipelineData.forEach((row) => {
        const s = row["Säljare"];
        if (s && s !== "Okänd" && ALLOWED_SELLERS.includes(s)) set.add(s);
      });
      return Array.from(set).sort();
    },
    velocity() {
      return this.salesVelocity[this.selectedSeller] || { meetingsPerWeek: 0, hitRate: 0, averageDealValue: 0 };
    },
    velocityHasData() {
      return this.velocity.meetingsPerWeek > 0 && this.velocity.hitRate > 0 && this.velocity.averageDealValue > 0;
    },
    velocityMetrics() {
      const expectedDeals = (this.velocity.meetingsPerWeek * this.velocity.hitRate) / 100;
      const expectedRevenueWeek = expectedDeals * this.velocity.averageDealValue;
      return {
        expectedDeals,
        expectedRevenueWeek,
        expectedRevenueMonth: expectedRevenueWeek * 4,
      };
    },
    sellerPipelineData() {
      if (!this.selectedSeller) return [];
      return this.pipelineData.filter((row) => row["Säljare"] === this.selectedSeller);
    },
    sellerWonDealsCurrentMonth() {
      if (!this.selectedSeller) return [];
      const now = new Date();
      const cm = now.getMonth();
      const cy = now.getFullYear();
      return this.wonDealsData.filter((row) => {
        if (row["Säljare"] !== this.selectedSeller) return false;
        if (!row["Avslutsdatum"]) return false;
        const d = new Date(row["Avslutsdatum"]);
        return d.getMonth() === cm && d.getFullYear() === cy;
      });
    },
    opportunitiesByPhase() {
      const phases = {};
      PHASE_ORDER.forEach((p) => { phases[p] = []; });

      this.sellerPipelineData.forEach((row) => {
        const fas = row["Fas"];
        if (!fas) return;
        const n = String(fas).toLowerCase().trim();
        if (n.includes("fallback")) phases["Fallback"].push(row);
        else if (n.includes("kvalificering")) phases["Kvalificering"].push(row);
        else if (n.includes("offert") || n.includes("andra möte")) phases["Offert/andra möte"].push(row);
        else if (n.includes("muntlig")) phases["Muntlig överenskommelse"].push(row);
      });

      this.sellerWonDealsCurrentMonth.forEach((row) => phases["Vunnen"].push(row));

      Object.keys(phases).forEach((phase) => {
        if (phase === "Vunnen") return;
        phases[phase].sort((a, b) => {
          const pa = getSortPriority(a["Avslutsdatum"]);
          const pb = getSortPriority(b["Avslutsdatum"]);
          if (pa !== pb) return pa - pb;
          const da = a["Avslutsdatum"] ? new Date(a["Avslutsdatum"]).getTime() : 0;
          const db = b["Avslutsdatum"] ? new Date(b["Avslutsdatum"]).getTime() : 0;
          return da - db;
        });
      });
      return phases;
    },
    phaseTotals() {
      const totals = {};
      Object.entries(this.opportunitiesByPhase).forEach(([phase, opps]) => {
        totals[phase] = opps.reduce((s, o) => s + (Number(o["Ordervärde"]) || 0), 0);
      });
      return totals;
    },
    firstMeetings() {
      if (!this.selectedSeller || !this.appointments.length) return [];
      const today = startOfDay(new Date());
      return this.appointments
        .filter((m) => {
          const sellers = m["Säljare"];
          if (!sellers) return false;
          const list = sellers.split(", ").map((s) => s.trim());
          if (!list.includes(this.selectedSeller)) return false;
          if (m["Mötestyp"] !== "Förstamöte") return false;
          if (!m["Mötesdatum"]) return false;
          return startOfDay(new Date(m["Mötesdatum"])) >= today;
        })
        .sort((a, b) => new Date(a["Mötesdatum"]) - new Date(b["Mötesdatum"]));
    },
    kpis() {
      const totalWonValue = this.sellerWonDealsCurrentMonth.reduce((s, r) => s + (Number(r["Ordervärde"]) || 0), 0);
      const budget = Number(this.budgets[this.selectedSeller]) || 0;
      return {
        totalWonValue,
        budget,
        budgetProgress: budget > 0 ? (totalWonValue / budget) * 100 : 0,
      };
    },
    sellerSelections() {
      return this.selectedOpportunities[this.selectedSeller] || { commit: new Set(), risk: new Set(), bestCase: new Set() };
    },
    forecastCalculations() {
      const wonValue = this.kpis.totalWonValue;
      const oppMap = new Map();
      Object.entries(this.opportunitiesByPhase).forEach(([phase, opps]) => {
        opps.forEach((opp) => {
          oppMap.set(getOppId(opp, phase), Number(opp["Ordervärde"]) || 0);
        });
      });
      const sumSet = (set) => {
        let total = 0;
        set.forEach((id) => { total += oppMap.get(id) || 0; });
        return total;
      };
      const commitTotal = sumSet(this.sellerSelections.commit);
      const riskTotal = sumSet(this.sellerSelections.risk);
      const bestCaseTotal = sumSet(this.sellerSelections.bestCase);
      return {
        commitValue: commitTotal,
        riskValue: riskTotal,
        bestCaseValue: bestCaseTotal,
        commitPlusWon: wonValue + commitTotal,
        worstCase: wonValue + commitTotal - riskTotal,
        bestCase: wonValue + commitTotal + bestCaseTotal,
      };
    },
    forecastHasData() {
      const s = this.sellerSelections;
      return s.commit.size > 0 || s.risk.size > 0 || s.bestCase.size > 0;
    },
    bestCaseWarning() {
      return this.forecastHasData
        && this.forecastCalculations.bestCaseValue < this.forecastCalculations.riskValue;
    },
    budgetRings() {
      const f = this.forecastCalculations;
      const b = this.kpis.budget;
      return [
        { label: "Vunnen", value: this.kpis.totalWonValue, percent: this.kpis.budgetProgress, color: "#EAB308" },
        { label: "Commit + Vunnen", value: f.commitPlusWon, percent: b > 0 ? (f.commitPlusWon / b) * 100 : 0, color: "#3B82F6" },
        { label: "Worst Case", value: f.worstCase, percent: b > 0 ? (f.worstCase / b) * 100 : 0, color: "#F97316" },
        { label: "Best Case", value: f.bestCase, percent: b > 0 ? (f.bestCase / b) * 100 : 0, color: "#10B981" },
      ];
    },
    opportunityIdValue() {
      const d = this.selectedItem?.data || {};
      return d["OpportunityID"] || d["Opportunity ID"] || d["OrderID"] || "";
    },
  },
  watch: {
    sellers: {
      immediate: true,
      handler(val) {
        if (!this.selectedSeller && val.length > 0) this.selectedSeller = val[0];
      },
    },
    selectedSeller() {
      this.editing = null;
      this.tempValue = "";
      this.budgetInput = (this.budgets[this.selectedSeller] || "").toString();
    },
    "forecastCalculations.commitValue": "syncForecastToStorage",
    "forecastCalculations.riskValue": "syncForecastToStorage",
    "forecastCalculations.bestCaseValue": "syncForecastToStorage",
    budgets: {
      handler(val) { this.persist(BUDGET_KEY, val); },
      deep: true,
    },
    salesVelocity: {
      handler(val) { this.persist(VELOCITY_KEY, val); },
      deep: true,
    },
    selectedOpportunities: {
      handler(val) {
        const out = {};
        Object.keys(val).forEach((seller) => {
          out[seller] = {
            commit: Array.from(val[seller].commit),
            risk: Array.from(val[seller].risk),
            bestCase: Array.from(val[seller].bestCase),
          };
        });
        this.persist(SELECTED_OPPS_KEY, out);
      },
      deep: true,
    },
  },
  created() {
    this.loadBudgets();
    this.loadVelocity();
    this.loadSelections();
  },
  methods: {
    formatNumber(n) {
      return Number(n || 0).toLocaleString("sv-SE", { maximumFractionDigits: 0 });
    },
    formatDate(s) {
      if (!s) return "-";
      return new Date(s).toLocaleDateString("sv-SE", { month: "short", day: "numeric" });
    },
    formatDateLong(s) {
      if (!s) return "–";
      return new Date(s).toLocaleDateString("sv-SE", { year: "numeric", month: "long", day: "numeric" });
    },
    persist(key, val) {
      try { localStorage.setItem(key, JSON.stringify(val)); } catch { /* ignore */ }
    },
    loadBudgets() {
      try {
        const stored = localStorage.getItem(BUDGET_KEY);
        if (stored) this.budgets = JSON.parse(stored);
      } catch { /* ignore */ }
    },
    loadVelocity() {
      try {
        const stored = localStorage.getItem(VELOCITY_KEY);
        if (stored) this.salesVelocity = JSON.parse(stored);
      } catch { /* ignore */ }
    },
    loadSelections() {
      try {
        const stored = localStorage.getItem(SELECTED_OPPS_KEY);
        if (!stored) return;
        const parsed = JSON.parse(stored);
        const out = {};
        Object.keys(parsed).forEach((seller) => {
          out[seller] = {
            commit: new Set(parsed[seller].commit || []),
            risk: new Set(parsed[seller].risk || []),
            bestCase: new Set(parsed[seller].bestCase || []),
          };
        });
        this.selectedOpportunities = out;
      } catch { /* ignore */ }
    },
    syncForecastToStorage() {
      if (!this.selectedSeller) return;
      try {
        const stored = localStorage.getItem(SALES_FORECAST_KEY);
        const existing = stored ? JSON.parse(stored) : {};
        existing[this.selectedSeller] = {
          commit: this.forecastCalculations.commitValue,
          riskInCommit: this.forecastCalculations.riskValue,
          strongBestCase: this.forecastCalculations.bestCaseValue,
        };
        localStorage.setItem(SALES_FORECAST_KEY, JSON.stringify(existing));
      } catch { /* ignore */ }
    },
    startEditBudget() {
      this.budgetInput = this.kpis.budget > 0 ? String(this.kpis.budget) : "";
      this.isEditingBudget = true;
    },
    saveBudget() {
      const value = parseFloat(this.budgetInput);
      if (!isNaN(value) && value >= 0 && this.selectedSeller) {
        this.$set(this.budgets, this.selectedSeller, value);
        this.isEditingBudget = false;
      }
    },
    startEdit(field) {
      this.editing = field;
      const map = { Meetings: "meetingsPerWeek", HitRate: "hitRate", AvgDeal: "averageDealValue" };
      this.tempValue = String(this.velocity[map[field]] || 0);
      this.$nextTick(() => { if (this.$refs.editInput) this.$refs.editInput.focus(); });
    },
    saveVelocity() {
      if (!this.selectedSeller || this.editing === null) { this.editing = null; return; }
      const map = { Meetings: "meetingsPerWeek", HitRate: "hitRate", AvgDeal: "averageDealValue" };
      const field = map[this.editing];
      let val = parseFloat(this.tempValue) || 0;
      if (this.editing === "HitRate") val = Math.max(0, Math.min(100, val));
      else val = Math.max(0, val);

      const current = this.salesVelocity[this.selectedSeller] || { meetingsPerWeek: 0, hitRate: 0, averageDealValue: 0 };
      this.$set(this.salesVelocity, this.selectedSeller, { ...current, [field]: val });
      this.editing = null;
      this.tempValue = "";
    },
    isInSelection(opp, phase, category) {
      const id = getOppId(opp, phase);
      return this.sellerSelections[category].has(id);
    },
    onOppClick(opp, phase) {
      if (!this.selectionMode || phase === "Vunnen") {
        this.selectedItem = { type: "opportunity", data: opp };
        return;
      }
      this.toggleOpportunity(getOppId(opp, phase), this.selectionMode);
    },
    toggleOpportunity(id, category) {
      if (!this.selectedSeller) return;
      const seller = this.selectedSeller;
      const current = this.selectedOpportunities[seller] || { commit: new Set(), risk: new Set(), bestCase: new Set() };
      const set = new Set(current[category]);
      const isRemoving = set.has(id);

      if (!isRemoving) {
        if (category === "risk" && !current.commit.has(id)) {
          this.$toasted.error("Risk i Commit kan endast vara affärer som är markerade som Commit", { duration: 3000 });
          return;
        }
        if (category === "bestCase" && current.commit.has(id)) {
          this.$toasted.error("Strong Best Case kan endast vara affärer som inte är markerade som Commit", { duration: 3000 });
          return;
        }
        set.add(id);
      } else {
        set.delete(id);
      }

      const newRisk = new Set(current.risk);
      const newBestCase = new Set(current.bestCase);
      if (category === "commit" && isRemoving && current.risk.has(id)) newRisk.delete(id);
      if (category === "commit" && !isRemoving && current.bestCase.has(id)) newBestCase.delete(id);

      const next = {
        ...this.selectedOpportunities,
        [seller]: {
          commit: category === "commit" ? set : current.commit,
          risk: category === "risk" ? set : newRisk,
          bestCase: category === "bestCase" ? set : newBestCase,
        },
      };
      this.selectedOpportunities = next;
    },
    resetForecasts() {
      if (!this.selectedSeller) return;
      this.selectedOpportunities = {
        ...this.selectedOpportunities,
        [this.selectedSeller]: { commit: new Set(), risk: new Set(), bestCase: new Set() },
      };
      this.selectionMode = null;
    },
    cardColorClass(dateStr, phase) {
      if (phase === "Vunnen") return "bg-yellow-50 border-yellow-400";
      if (!dateStr) return "bg-gray-50 border-gray-200";
      const closeDate = startOfDay(new Date(dateStr));
      const now = startOfDay(new Date());
      if (closeDate < now) return "bg-red-50 border-red-300";
      if (closeDate.getMonth() === now.getMonth() && closeDate.getFullYear() === now.getFullYear()) {
        return "bg-green-50 border-green-300";
      }
      return "bg-gray-50 border-gray-200";
    },
    dashOffset(value) {
      const b = this.kpis.budget;
      const pct = b > 0 ? Math.min((value / b) * 100, 100) : 0;
      return this.circumference * (1 - pct / 100);
    },
  },
};
</script>
